import { useRef, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HiXMark } from 'react-icons/hi2';
import { Button } from '../../../components/Button';
import { Select } from '../../../components/Select';

interface FiltersFlyoutProps {
  isOpen: boolean;
  onClose: () => void;
  anchorElement: HTMLElement | null;
  
  // Filtros
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  statusFilter: string;
  setStatusFilter: (value: string) => void;
  priorityFilter: string;
  setPriorityFilter: (value: string) => void;
  processoId: string;
  setProcessoId: (value: string) => void;
  processoOptions: Array<{ value: string | number; label: string }>;
  clearFilters: () => void;
}

export const FiltersFlyout: React.FC<FiltersFlyoutProps> = ({
  isOpen,
  onClose,
  anchorElement,
  searchTerm,
  setSearchTerm,
  statusFilter,
  setStatusFilter,
  priorityFilter,
  setPriorityFilter,
  processoId,
  setProcessoId,
  processoOptions,
  clearFilters,
}) => {
  const flyoutRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; right: number }>({ top: 0, right: 0 });
  
  // Estados temporários para os filtros
  const [tempSearchTerm, setTempSearchTerm] = useState(searchTerm);
  const [tempStatusFilter, setTempStatusFilter] = useState(statusFilter);
  const [tempPriorityFilter, setTempPriorityFilter] = useState(priorityFilter);
  const [tempProcessoId, setTempProcessoId] = useState(processoId);

  // Sincronizar filtros temporários quando o flyout abrir
  useEffect(() => {
    if (isOpen) {
      setTempSearchTerm(searchTerm);
      setTempStatusFilter(statusFilter);
      setTempPriorityFilter(priorityFilter);
      setTempProcessoId(processoId);
    }
  }, [isOpen, searchTerm, statusFilter, priorityFilter, processoId]);

  useEffect(() => {
    if (isOpen && anchorElement) {
      const rect = anchorElement.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right
      });
    }
  }, [isOpen, anchorElement]);

  // Fechar ao clicar fora (removido para evitar fechar ao interagir com selects)

  // Fechar com Escape
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
    
    return undefined;
  }, [isOpen, onClose]);

  const handleStatusChange = (value: string | number) => {
    setTempStatusFilter(String(value));
  };

  const handlePriorityChange = (value: string | number) => {
    setTempPriorityFilter(String(value));
  };

  const handleApplyFilters = () => {
    setSearchTerm(tempSearchTerm);
    setStatusFilter(tempStatusFilter);
    setPriorityFilter(tempPriorityFilter);
    setProcessoId(tempProcessoId);
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  const handleClearFilters = () => {
    setTempSearchTerm('');
    setTempStatusFilter('');
    setTempPriorityFilter('');
    setTempProcessoId('');
    clearFilters();
    onClose();
  };

  const statusOptions = [
    { value: '', label: 'Todos os status' },
    { value: 'EMATRIBUICAO', label: 'Em Atribuição' },
    { value: 'ATRIBUIDA', label: 'Atribuída' },
    { value: 'EMPROGRESSO', label: 'Em Progresso' },
    { value: 'AGUARDANDOAUTORIZACAO', label: 'Aguardando Autorização' },
    { value: 'SOLUCIONADA', label: 'Solucionada' },
    { value: 'FINALIZADA', label: 'Finalizada' },
    { value: 'DEVOLVIDA', label: 'Devolvida' },
    { value: 'ERRADA', label: 'Errada' },
    { value: 'RECUSADA', label: 'Recusada' },
    // { value: 'EXCLUIDA', label: 'Excluída' },
  ];

  const priorityOptions = [
    { value: '', label: 'Todas as prioridades' },
    { value: 'Baixa', label: 'Baixa' },
    { value: 'Media', label: 'Média' },
    { value: 'Alta', label: 'Alta' },
    { value: 'Urgente', label: 'Urgente' },
  ];

  const hasActiveFilters = tempSearchTerm || tempStatusFilter || tempPriorityFilter || tempProcessoId;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={flyoutRef}
          initial={{ opacity: 0, scale: 0.95, y: -10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: -10 }}
          transition={{ 
            duration: 0.2, 
            ease: [0.4, 0, 0.2, 1]
          }}
          style={{
            position: 'fixed',
            top: position.top,
            right: position.right,
            zIndex: 50
          }}
          className="w-[600px] max-h-[calc(100vh-100px)] overflow-hidden"
        >
          <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-2xl border border-gray-200 dark:border-neutral-700 overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-neutral-700 bg-gray-50 dark:bg-neutral-900">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                Filtros Avançados
              </h3>
              <button
                onClick={onClose}
                className="p-1 rounded-md hover:bg-gray-200 dark:hover:bg-neutral-700 transition-colors"
              >
                <HiXMark className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4 overflow-y-auto max-h-[calc(100vh-200px)]">
              {/* Campo de Busca */}
              <div className="mb-4">
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
                    Buscar
                  </label>
                  <input
                    type="text"
                    value={tempSearchTerm}
                    onChange={(e) => setTempSearchTerm(e.target.value)}
                    placeholder="Buscar por título, número, processo, demandante..."
                    className="h-9 border border-gray-300 dark:border-neutral-600 rounded-md px-3 py-1 bg-white dark:bg-neutral-800 text-gray-900 dark:text-gray-100 text-xs focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    A busca procura em vários campos da demanda
                  </p>
                </div>
              </div>

              {/* Filtros principais */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                {/* Status */}
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Status</label>
                  <Select
                    value={tempStatusFilter}
                    onChange={handleStatusChange}
                    options={statusOptions}
                    placeholder="Todos os status"
                    className="h-9 text-xs"
                  />
                </div>
                
                {/* Prioridade */}
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Prioridade</label>
                  <Select
                    value={tempPriorityFilter}
                    onChange={handlePriorityChange}
                    options={priorityOptions}
                    placeholder="Todas as prioridades"
                    className="h-9 text-xs"
                  />
                </div>
              </div>

              {/* Processo */}
              <div className="mb-4">
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Processo</label>
                  <Select
                    value={tempProcessoId}
                    onChange={(v) => setTempProcessoId(String(v))}
                    options={processoOptions}
                    placeholder="Selecionar processo"
                    className="h-9 text-xs"
                  />
                </div>
              </div>
            </div>

            {/* Footer - Sempre visível com botões de ação */}
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="px-4 py-3 border-t border-gray-200 dark:border-neutral-700 bg-gray-50 dark:bg-neutral-900 flex justify-between items-center"
            >
              <div className="flex items-center gap-2">
                {hasActiveFilters && (
                  <>
                    <span className="text-xs text-gray-600 dark:text-gray-400">
                      {[tempSearchTerm, tempStatusFilter, tempPriorityFilter, tempProcessoId]
                        .filter(Boolean).length} filtro(s) selecionado(s)
                    </span>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={handleClearFilters} 
                      className="flex items-center gap-1 h-7 px-2 text-xs text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
                    >
                      <HiXMark className="w-3 h-3" />
                      Limpar
                    </Button>
                  </>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleCancel} 
                  className="h-8 px-3 text-xs"
                >
                  Cancelar
                </Button>
                <Button 
                  variant="primary" 
                  size="sm" 
                  onClick={handleApplyFilters} 
                  className="h-8 px-4 text-xs bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Buscar
                </Button>
              </div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
